import React, { useState, useEffect, useCallback } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Container,
  Typography,
  Tooltip,
  TextField,
  Button,
  Alert,
  Table,
  Popover,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Switch,
  MenuItem,
  Paper,
} from "@mui/material";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import EditOutlined from "@mui/icons-material/EditOutlined";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError, handleToggleError } from "../utils/errorHandler";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
function Environments() {
  const [formData, setFormData] = useState({
    cl_id: "",
    // client_name: "",
    env_id: "",
    name: "",
    active_flag: 1,
    created_by: "",
    created_date: "",
    updated_by: "",
    updated_date: "",
  });
  const [visibleColumns, setVisibleColumns] = useState({
    // cl_id: false,
    client_name: true,
    env_id: true,
    name: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Added for error handling
  const [environments, setEnvironments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [selectedEnvIds, setSelectedEnvIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false); // Add loading state
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    name: "",
  });
  const [tempFilters, setTempFilters] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    name: "",
  });
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const columnLabels = {
    cl_id: "Client ID",
    client_name: "Client",
    env_id: "Environment ID",
    name: "Environment",
  };
  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const validate = () => {
    const errors = {};
    const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/; // Only special characters
    const onlyNumbersRegex = /^[0-9]+$/; // Only numbers
    const noLettersRegex = /^[^a-zA-Z]+$/; // No letters (only numbers and/or special chars)

    // Environment ID validation
    const trimmedEnvId = formData.env_id.trim();
    if (!trimmedEnvId) {
      errors.env_id = "Environment ID is required.";
    } else if (onlySpecialCharRegex.test(trimmedEnvId)) {
      errors.env_id = "Environment ID cannot contain only special characters.";
    } else if (onlyNumbersRegex.test(trimmedEnvId)) {
      errors.env_id = "Environment ID cannot contain only numbers.";
    } else if (noLettersRegex.test(trimmedEnvId)) {
      errors.env_id = "Environment ID must contain at least one letter.";
    } else if (!isEditing) {
      // Check for duplicate Environment ID only when adding new
      const isDuplicate = data.some(
        (env) => env.env_id.toLowerCase() === trimmedEnvId.toLowerCase(),
      );
      if (isDuplicate) {
        errors.env_id = "Environment ID already exists.";
      }
    }

    // Name validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      errors.name = "Name is required.";
    } else if (onlySpecialCharRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only special characters.";
    } else if (onlyNumbersRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only numbers.";
    } else if (noLettersRegex.test(trimmedName)) {
      errors.name = "Name must contain at least one letter.";
    }

    return errors;
  };

  const fetchEnvironments = useCallback(async () => {
    setLoading(true); // Start loading
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    // Add search term if present
    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm.trim());
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });

    try {
      const result = await apiService.get(
        `/environments/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false); // End loading
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const fetchClients = async () => {
    try {
      const data = await apiService.get("/clients");
      setClients(data);
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients, formData.cl_id]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      // Trim all string fields before submission
      const payload = {
        cl_id: formData.cl_id,
        client_name: formData.client_name,
        env_id: formData.env_id.trim(),
        name: formData.name.trim(),
      };

      if (isEditing) {
        await apiService.put(`/environments/${formData.env_id}`, payload);
      } else {
        await apiService.post("/environments", payload);
      }

      setSuccessMessage(
        isEditing
          ? "Environment updated successfully!"
          : "Environment created successfully!",
      );

      // Reset form data properly
      const defaultClient = clients.length > 0 ? clients[0].cl_id : "";
      const defaultClientName = clients.length > 0 ? clients[0].name : "";

      setFormData({
        cl_id: defaultClient,
        client_name: defaultClientName,
        env_id: "",
        name: "",
        active_flag: 1,
        created_by: "",
        created_date: "",
        updated_by: "",
        updated_date: "",
      });
      setFormErrors({});
      setIsEditing(false);
      setOpenDialog(false); // Close dialog after successful submission
      fetchEnvironments();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(err, "Environment")
        : handleCreateError(err, "Environment");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleEdit = (env) => {
    setOriginalFormData({ ...env });
    setFormData(env);
    setIsEditing(true);
    setIsFormChanged(false);
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDelete = (cl_id, env_id) => {
    setDeleteData({ cl_id, env_id });
    setOpenDeleteDialog(true);
  };

  const handleBulkDelete = () => {
    if (selectedEnvIds.length === 0) return;

    setDeleteData({ bulk: true });
    setOpenDeleteDialog(true);
  };

  const handleDialogOpen = () => {
    // Pre-select first client
    const defaultClient = clients.length > 0 ? clients[0].cl_id : "";
    const defaultClientName = clients.length > 0 ? clients[0].name : "";

    setFormData({
      cl_id: defaultClient,
      client_name: defaultClientName,
      env_id: "",
      name: "",
      active_flag: 1,
      created_by: "",
      created_date: "",
      updated_by: "",
      updated_date: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    // Reset form data and editing state when dialog closes
    setFormData({
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      name: "",
      active_flag: 1,
      created_by: "",
      created_date: "",
      updated_by: "",
      updated_date: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setOriginalFormData({});
    setIsFormChanged(false);
  };

  const columns = ["client_name", "env_id", "name"];

  const handleToggleFlag = async (env_id, active_flag) => {
    try {
      const newFlag = active_flag === 1 ? 0 : 1;

      await apiService.put(`/environments/${env_id}`, { active_flag: newFlag });

      fetchEnvironments(); // Refresh the table

      // Show success message
      const message =
        newFlag === 1 ? "Environment Activated" : "Environment Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      // Use centralized error handler
      const errorMessage = handleToggleError(error, "Environment");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Environments Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Environments
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={handleDialogOpen}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {selectedEnvIds.length > 0 && (
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDelete}
                sx={{
                  height: "35px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                Delete Selected
              </Button>
            )}

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    // Show loading message
                    <TableRow>
                      <TableCell 
                        colSpan={10} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((env) => (
                      <TableRow key={env.env_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  env.env_id,
                                  parseInt(env.active_flag),
                                )
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(env.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(env)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDelete(env.cl_id, env.env_id)
                              }
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.client_name && (
                          <TableCell>{env.client_name}</TableCell>
                        )}
                        {visibleColumns.env_id && (
                          <TableCell>{env.env_id}</TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell>{env.name}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={10} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No environments found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>

        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* Dialog for Create/Edit */}
          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            width="small"
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit Environment" : "Add Environment"}
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                {/* Frozen Client Field - Read-only with gray background */}
                <TextField
                  fullWidth
                  label="Client"
                  value={
                    formData.client_name ||
                    clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                    ""
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    backgroundColor: "white",

                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      color: "grey",
                    },
                  }}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                {/* Environment ID - Frozen in edit mode, editable in add mode */}
                <TextField
                  fullWidth
                  label="Environment ID"
                  name="env_id"
                  value={formData.env_id}
                  onChange={handleChange}
                  required
                  error={!!formErrors.env_id}
                  helperText={formErrors.env_id}
                  margin="normal"
                  sx={{
                    minWidth: 300,
                    ...(isEditing && {
                      backgroundColor: "white",
                      "& .MuiInputBase-input": {
                        cursor: "not-allowed",
                        color: "grey",
                      },
                    }),
                  }}
                  InputProps={{
                    readOnly: isEditing,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  margin="normal"
                  sx={{ minWidth: 140 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>

              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isEditing && !isFormChanged}
                //  disabled={loading}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDeleteDialog(false);
              setDeleteData(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Environment{" "}
                <strong>
                  {data.find((env) => env.env_id === deleteData?.env_id)?.name}
                </strong>{" "}
                ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteData(null);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await apiService.delete(
                      `/environments/${deleteData.env_id}`,
                    );

                    setSuccessMessage("Environment deleted successfully!");
                    setTimeout(() => setSuccessMessage(""), 3000);

                    fetchEnvironments();
                  } catch (err) {
                    // Use centralized error handler
                    const errorMessage = handleDeleteError(err, "Environment");
                    setErrorMessage(errorMessage);
                    setTimeout(() => setErrorMessage(""), 5000);
                  } finally {
                    setOpenDeleteDialog(false);
                    setDeleteData(null);
                  }
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default Environments;
